import { defineStore } from 'pinia'
import { store } from '../index'
import type { LoginReq, RegisterReq, UserInfo } from '@/api/user/types'
import { loginApi, registerApi, getProfileApi, updateProfileApi } from '@/api/user'
import { getFileSystemTokenApi } from '@/api/system/filesystem'
import { ElMessage } from 'element-plus'

const STORE_KEY = 'user-store'

/** 从 localStorage 恢复状态 */
function loadState(): { token: string; userInfo: UserInfo | null; rememberMe: boolean; loginInfo: LoginReq | null; imageDomain: string } {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (raw) {
      const data = JSON.parse(raw)
      return {
        token: data.token || '',
        userInfo: data.userInfo || null,
        rememberMe: data.rememberMe ?? true,
        loginInfo: data.loginInfo || null,
        imageDomain: data.imageDomain || '',
      }
    }
  } catch { /* ignore */ }
  return { token: '', userInfo: null, rememberMe: true, loginInfo: null, imageDomain: '' }
}

/** 持久化到 localStorage */
function saveState(state: { token: string; userInfo: UserInfo | null; rememberMe: boolean; loginInfo: LoginReq | null; imageDomain: string }) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({
      token: state.token,
      userInfo: state.userInfo,
      rememberMe: state.rememberMe,
      loginInfo: state.loginInfo,
      imageDomain: state.imageDomain,
    }))
  } catch { /* ignore */ }
}

interface UserState {
  token: string
  userInfo: UserInfo | null
  rememberMe: boolean
  loginInfo: LoginReq | null
  imageDomain: string
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return loadState()
  },
  getters: {
    getToken(): string {
      return this.token
    },
    getUserInfo(): UserInfo | null {
      return this.userInfo
    },
    getRememberMe(): boolean {
      return this.rememberMe
    },
    getLoginInfo(): LoginReq | null {
      return this.loginInfo
    },
    getImageDomain(): string {
      return this.imageDomain
    },
    isLoggedIn(): boolean {
      return !!this.token
    },
  },
  actions: {
    /** 设置 Token */
    setToken(token: string) {
      this.token = token
      saveState(this.$state)
    },

    /** 设置用户信息 */
    setUserInfo(userInfo: UserInfo | null) {
      this.userInfo = userInfo
      saveState(this.$state)
    },

    /** 设置图片域名 */
    setImageDomain(domain: string) {
      this.imageDomain = domain
      sessionStorage.setItem('imageDomain', domain)
      saveState(this.$state)
    },

    /** 从文件系统 API 获取媒体访问域名（httpUrl）并持久化 */
    async fetchImageDomain() {
      try {
        const res = await getFileSystemTokenApi()
        if (res.status === 'success' && res.result?.httpUrl) {
          let url = res.result.httpUrl
          // 去掉末尾的 /
          if (url.endsWith('/')) url = url.slice(0, -1)
          this.setImageDomain(url)
        }
      } catch { /* 非关键，失败不影响登录 */ }
    },

    /** 设置记住我 */
    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe
      saveState(this.$state)
    },

    /** 设置登录凭据 */
    setLoginInfo(loginInfo: LoginReq | null) {
      this.loginInfo = loginInfo
      saveState(this.$state)
    },

    /** 登录 */
    async login(data: LoginReq): Promise<boolean> {
      try {
        const res = await loginApi(data)
        if (res.status === 'success') {
          // 接口偶发返回 status=success 但 result 为空（响应被网关/登录页替换）。
          // 直接解构会抛 "Cannot destructure property 'token' of 'res.result' as it is null"，
          // 这里先校验，给出可读提示。
          const result = res.result
          if (!result || typeof result !== 'object' || !result.token) {
            ElMessage.error('登录响应异常，请重试')
            return false
          }
          const { token, userInfo, refresh_token } = result
          this.setToken(token)
          this.setUserInfo(userInfo)
          sessionStorage.setItem('Authorization', token)
          sessionStorage.setItem('userInfo', JSON.stringify(userInfo))
          if (refresh_token) sessionStorage.setItem('oidc_refresh_token', refresh_token)
          ElMessage.success('登录成功')
          this.fetchImageDomain()
          return true
        }
        ElMessage.error(res.message || '登录失败，请检查账号和密码')
        return false
      } catch (error: any) {
        console.error('登录失败:', error)
        ElMessage.error(error?.message || '登录失败，请检查账号和密码')
        return false
      }
    },

    /** 注册 */
    async register(data: RegisterReq): Promise<{ success: boolean; message?: string }> {
      try {
        const res = await registerApi(data)
        if (res.status === 'success') {
          return { success: true }
        }
        // 后端返回 { status: "error", message: "..." }，直接透传 message
        return { success: false, message: res.message || '注册失败' }
      } catch (error: any) {
        console.error('注册失败:', error)
        return { success: false, message: error?.message || '注册失败，请重试' }
      }
    },

    /** 获取用户资料 */
    async fetchProfile(): Promise<boolean> {
      try {
        const res = await getProfileApi()
        if (res.status === 'success') {
          // 与已有数据合并，优先用 API 返回值，缺失字段保留本地数据
          const merged = { ...this.userInfo, ...res.result }
          this.setUserInfo(merged)
          if (!this.imageDomain) this.fetchImageDomain()
          return true
        }
        return false
      } catch (error: any) {
        console.error('获取用户资料失败:', error)
        return false
      }
    },

    /** 更新个人资料——全量回传所有字段，防止后端清空未传字段 */
    async updateProfile(data: Partial<UserInfo>): Promise<boolean> {
      try {
        // 基于当前 userInfo 合并变更，全量提交
        if (!this.userInfo) return false
        const fullData = { ...this.userInfo, ...data }
        const res = await updateProfileApi(fullData)
        if (res.status === 'success') {
          Object.assign(this.userInfo, data)
          saveState(this.$state)
          ElMessage.success('保存成功')
          return true
        }
        ElMessage.error(res.message || '保存失败')
        return false
      } catch (error: any) {
        console.error('更新资料失败:', error)
        ElMessage.error(error?.message || '保存失败')
        return false
      }
    },

    /** 登出 */
    logout() {
      this.token = ''
      this.userInfo = null
      this.imageDomain = ''
      // 清除持久化
      localStorage.removeItem(STORE_KEY)
      sessionStorage.removeItem('Authorization')
      sessionStorage.removeItem('userInfo')
      sessionStorage.removeItem('imageDomain')
    },
  },
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
